import { useEffect, useMemo, useState, type DragEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  GripVertical,
  type LucideIcon,
  Phone,
  RefreshCw,
  Route,
  ShieldCheck,
  Timer,
  UserRound
} from 'lucide-react'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import { getErrorMessage } from '../../services/errorUtils'

type StatusKind = 'success' | 'failed' | 'declined' | 'active' | 'pending' | 'simulated' | 'unknown'

interface PipelineAttempt {
  id: string
  status_kind: StatusKind
  status_label: string
  duration_label?: string | null
  created_at?: string | null
  failure_reason?: string | null
  sip_status?: number | null
}

interface PipelineCard {
  id: string
  source_id: string
  lead_key?: string
  column_id: string
  title: string
  phone_number?: string | null
  agent_id?: string | null
  agent_name?: string | null
  agent_names?: string[]
  status_kind: StatusKind
  status_label: string
  duration_label?: string | null
  latest_duration_label?: string | null
  created_at?: string | null
  first_call_at?: string | null
  last_call_at?: string | null
  last_synced_at?: string | null
  latest_call_id?: string | null
  attempts?: number
  success_count?: number
  failed_count?: number
  declined_count?: number
  summary?: string | null
  next_action?: string | null
  failure_reason?: string | null
  sip_status?: number | null
  diagnostic_hint?: string | null
  calls?: PipelineAttempt[]
  tags?: string[]
  preflight?: {
    status: 'approved' | 'attention'
    checked_at: string
    message: string
  } | null
}

interface PipelineColumn {
  id: string
  title: string
  tone: string
  description: string
  count: number
  cards: PipelineCard[]
}

interface PipelineStats {
  total: number
  success: number
  failed: number
  declined?: number
  success_rate: number
}

interface PipelineBoard {
  columns: PipelineColumn[]
  stats: PipelineStats
  updated_at?: string | null
  last_sync_at?: string | null
  meta?: {
    synced_count?: number
    sync_error?: string
    stale?: boolean
  }
}

const columnToneClass: Record<string, string> = {
  slate: 'border-slate-200 bg-white',
  blue: 'border-blue-200 bg-white',
  indigo: 'border-indigo-200 bg-white',
  amber: 'border-amber-200 bg-white',
  emerald: 'border-emerald-200 bg-white',
  cyan: 'border-cyan-200 bg-white',
  green: 'border-green-200 bg-white',
  rose: 'border-rose-200 bg-white',
  red: 'border-red-200 bg-white'
}

const columnAccentClass: Record<string, string> = {
  slate: 'bg-slate-400',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  amber: 'bg-amber-500',
  emerald: 'bg-emerald-500',
  cyan: 'bg-cyan-500',
  green: 'bg-green-500',
  rose: 'bg-rose-500',
  red: 'bg-red-500'
}

// Maps StatusKind to design-system badge classes
const statusBadgeClass: Record<StatusKind, string> = {
  success:   'badge badge-success',
  failed:    'badge badge-danger',
  declined:  'badge badge-danger opacity-75',
  active:    'badge badge-info',
  pending:   'badge badge-warning',
  simulated: 'badge bg-slate-100 text-slate-600',
  unknown:   'badge bg-slate-100 text-slate-500'
}

function formatDate(value?: string | null) {
  if (!value) return 'Sem data'
  return new Date(value).toLocaleString('pt-BR')
}

function formatShortDate(value?: string | null) {
  if (!value) return 'Sem data'
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export default function Pipeline() {
  const navigate = useNavigate()
  const [board, setBoard] = useState<PipelineBoard | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [draggedCardId, setDraggedCardId] = useState<string | null>(null)
  const [preflightingCardId, setPreflightingCardId] = useState<string | null>(null)

  const columns = useMemo(() => board?.columns || [], [board])

  const loadBoard = async (refresh = false) => {
    try {
      setLoading(true)
      setLoadError(false)
      const response = await apiService.getPipelineBoard(refresh)
      setBoard(response.data.data)
      if (response.data.data?.meta?.sync_error) {
        toast.error(response.data.data.meta.sync_error)
      }
    } catch (error) {
      setLoadError(true)
      toast.error(getErrorMessage(error, 'Erro ao carregar pipeline'))
    } finally {
      setLoading(false)
    }
  }

  const syncBoard = async () => {
    try {
      setSyncing(true)
      const response = await apiService.syncPipeline(50)
      setBoard(response.data.data)
      const count = response.data.data?.meta?.synced_count || 0
      toast.success(`${count} tentativas sincronizadas por lead`)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erro ao sincronizar pipeline'))
    } finally {
      setSyncing(false)
    }
  }

  useEffect(() => {
    void loadBoard()
  }, [])

  const handleDragStart = (event: DragEvent<HTMLElement>, cardId: string) => {
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', cardId)
    setDraggedCardId(cardId)
  }

  const handleDrop = async (event: DragEvent<HTMLElement>, column: PipelineColumn) => {
    event.preventDefault()
    const cardId = event.dataTransfer.getData('text/plain') || draggedCardId
    setDraggedCardId(null)
    if (!cardId) return

    const previousBoard = board
    try {
      const response = await apiService.movePipelineCard(cardId, {
        column_id: column.id,
        position: column.cards.length
      })
      setBoard(response.data.data)
    } catch (error) {
      setBoard(previousBoard)
      toast.error(getErrorMessage(error, 'Erro ao mover card'))
    }
  }

  const handlePreflight = async (card: PipelineCard) => {
    if (!card.agent_id || !card.phone_number) {
      toast.error('Card sem agente ou telefone para preflight')
      return
    }

    try {
      setPreflightingCardId(card.id)
      const response = await apiService.preflightPipelineCard(card.id)
      setBoard(response.data.data.board)
      toast.success('Preflight aprovado')
    } catch (error) {
      toast.error(getErrorMessage(error, 'Preflight falhou'))
    } finally {
      setPreflightingCardId(null)
    }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="page-shell">
        {/* Skeleton header */}
        <div className="page-header">
          <div>
            <div className="h-8 w-28 animate-pulse rounded-lg bg-slate-200" />
            <div className="mt-2 h-4 w-80 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-32 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-10 w-32 animate-pulse rounded-lg bg-slate-200" />
            <div className="h-10 w-36 animate-pulse rounded-lg bg-slate-200" />
          </div>
        </div>
        {/* Skeleton metrics */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="metric-card flex items-center gap-3">
              <div className="metric-icon animate-pulse bg-slate-100" />
              <div className="space-y-2">
                <div className="h-3 w-20 animate-pulse rounded bg-slate-100" />
                <div className="h-6 w-12 animate-pulse rounded bg-slate-200" />
              </div>
            </div>
          ))}
        </div>
        {/* Skeleton columns */}
        <div className="-mx-4 flex gap-4 overflow-x-hidden px-4 pb-4 sm:mx-0 sm:px-0">
          {Array.from({ length: 4 }).map((_, colIdx) => (
            <div
              key={colIdx}
              className="flex h-[38rem] min-w-[280px] w-72 flex-none flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm"
            >
              <div className="h-1.5 animate-pulse bg-slate-200" />
              <div className="border-b border-slate-100 bg-slate-50 px-3 py-3">
                <div className="h-4 w-24 animate-pulse rounded bg-slate-200" />
                <div className="mt-2 h-3 w-40 animate-pulse rounded bg-slate-100" />
              </div>
              <div className="flex-1 space-y-3 overflow-hidden p-3">
                {Array.from({ length: 3 }).map((__, cardIdx) => (
                  <div key={cardIdx} className="rounded-lg border border-slate-100 bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
                        <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
                      </div>
                      <div className="h-5 w-16 animate-pulse rounded-full bg-slate-100" />
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-2">
                      {Array.from({ length: 4 }).map((___, k) => (
                        <div key={k} className="h-10 animate-pulse rounded-md bg-slate-50" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (loadError || board === null) {
    return (
      <div className="page-shell">
        <div className="empty-state py-20">
          <AlertCircle className="h-12 w-12 text-danger-400 mb-4" />
          <h3 className="text-base font-semibold text-slate-900 mb-2">Não foi possível carregar o pipeline</h3>
          <p className="mb-6 text-sm text-slate-500">Verifique sua conexão e tente novamente.</p>
          <button
            type="button"
            onClick={() => void loadBoard(true)}
            className="btn-secondary"
          >
            <RefreshCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="page-shell">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Pipeline</h1>
          <p className="page-description">
            Organize cada lead pelo número de telefone, acompanhe tentativas e mova oportunidades entre etapas.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-600 shadow-sm">
            Sync: {board?.last_sync_at ? formatShortDate(board.last_sync_at) : 'pendente'}
          </div>
          <button
            type="button"
            onClick={() => void loadBoard(true)}
            className="btn-secondary"
            aria-label="Atualizar pipeline"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
          <button
            type="button"
            onClick={syncBoard}
            disabled={syncing}
            className="btn-primary"
            aria-label="Sincronizar pipeline"
          >
            {syncing
              ? <RefreshCw className="h-4 w-4 animate-spin" />
              : <Route className="h-4 w-4" />
            }
            Sincronizar
          </button>
        </div>
      </div>

      {/* Stats metrics */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Metric icon={Phone} label="Leads" value={board?.stats.total || 0} iconBg="bg-primary-50 text-primary-600" />
        <Metric icon={CheckCircle2} label="Taxa de sucesso" value={`${board?.stats.success_rate || 0}%`} iconBg="bg-success-50 text-success-600" />
        <Metric icon={AlertTriangle} label="Falhas" value={board?.stats.failed || 0} iconBg="bg-danger-50 text-danger-600" />
        <Metric icon={Clock3} label="Última sync" value={board?.last_sync_at ? formatDate(board.last_sync_at) : 'Pendente'} iconBg="bg-slate-100 text-slate-500" compact />
      </div>

      {board?.meta?.sync_error && (
        <div className="rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-900">
          {board.meta.sync_error}
        </div>
      )}

      {/* Kanban board */}
      <div className="scrollbar-thin -mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
        {columns.map((column) => (
          <section
            key={column.id}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => void handleDrop(event, column)}
            className={`flex h-[38rem] min-w-[280px] w-[min(23rem,calc(100vw-2rem))] flex-none snap-start flex-col overflow-hidden rounded-lg border shadow-sm sm:w-[23rem] ${columnToneClass[column.tone] || columnToneClass.slate}`}
          >
            {/* Column accent stripe */}
            <div className={`h-1.5 flex-none ${columnAccentClass[column.tone] || columnAccentClass.slate}`} />

            {/* Column header */}
            <div className="flex-none border-b border-slate-100 bg-slate-50/80 px-3 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="truncate text-sm font-semibold text-slate-950">{column.title}</h2>
                  <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{column.description}</p>
                </div>
                <span className="badge bg-slate-100 text-slate-600 flex-none">
                  {column.count}
                </span>
              </div>
            </div>

            {/* Cards */}
            <div className="scrollbar-thin min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50/60 p-3">
              {column.cards.length === 0 ? (
                <div className="flex h-24 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white/70 text-center text-xs font-medium text-slate-400">
                  Solte cards aqui
                </div>
              ) : (
                column.cards.map((card) => (
                  <article
                    key={card.id}
                    draggable
                    aria-label={`Lead ${card.title}`}
                    onDragStart={(event) => handleDragStart(event, card.id)}
                    onDragEnd={() => setDraggedCardId(null)}
                    className={`card cursor-grab p-3 transition hover:border-primary-200 hover:shadow-md active:cursor-grabbing ${
                      draggedCardId === card.id
                        ? 'opacity-50 ring-2 ring-primary-400'
                        : ''
                    }`}
                  >
                    {/* Card header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2">
                        <GripVertical className="mt-0.5 h-4 w-4 flex-none text-slate-300" />
                        <div className="min-w-0">
                          <h3 className="truncate text-sm font-semibold text-slate-950">{card.phone_number || card.title}</h3>
                          <p className="mt-0.5 truncate font-mono text-xs text-slate-500">
                            {card.latest_call_id || 'sem ID de ligação'}
                          </p>
                        </div>
                      </div>
                      <span className={`flex-none ${statusBadgeClass[card.status_kind]}`}>
                        {card.status_label}
                      </span>
                    </div>

                    {/* Lead metrics mini-grid */}
                    <div className="mt-3 grid grid-cols-4 gap-2 text-center text-xs">
                      <LeadMetric label="Tentativas" value={card.attempts || 0} />
                      <LeadMetric label="Sucesso" value={card.success_count || 0} />
                      <LeadMetric label="Falhas" value={card.failed_count || 0} />
                      <LeadMetric label="Recusadas" value={card.declined_count || 0} />
                    </div>

                    {/* Info lines */}
                    <div className="mt-3 space-y-1.5 text-xs text-slate-600">
                      <InfoLine icon={UserRound} text={card.agent_name || card.agent_names?.[0] || 'Agente sem nome'} />
                      <InfoLine icon={Timer} text={`Total ${card.duration_label || '0s'} · última ${card.latest_duration_label || '0s'}`} />
                      <InfoLine icon={Clock3} text={`Última: ${formatDate(card.last_call_at || card.created_at)}`} />
                    </div>

                    {/* Summary */}
                    {card.summary && (
                      <p className="mt-3 line-clamp-3 break-words rounded-lg bg-slate-50 px-2 py-2 text-xs leading-5 text-slate-700">{card.summary}</p>
                    )}

                    {/* Failure/declined block */}
                    {['failed', 'declined'].includes(card.status_kind) && (
                      <div className={`mt-3 rounded-r-lg border-l-4 px-2 py-2 text-xs ${
                        card.status_kind === 'declined'
                          ? 'border-warning-400 bg-warning-50 text-warning-900'
                          : 'border-danger-400 bg-danger-50 text-danger-900'
                      }`}>
                        <div className="break-words font-medium">
                          {card.failure_reason || (card.status_kind === 'declined' ? 'Chamada recusada pelo destinatário.' : 'Falha sem motivo técnico detalhado.')}
                        </div>
                        {card.sip_status && <div className="mt-1 font-mono">SIP {card.sip_status}</div>}
                        {card.diagnostic_hint && <div className="mt-1 break-words text-slate-600">{card.diagnostic_hint}</div>}
                      </div>
                    )}

                    {/* Recent attempts */}
                    {card.calls && card.calls.length > 0 && (
                      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-2 py-2">
                        <div className="mb-2 text-xs font-semibold text-slate-600">Últimas tentativas</div>
                        <div className="space-y-2">
                          {card.calls.slice(0, 3).map((attempt) => (
                            <div key={attempt.id} className="flex items-start justify-between gap-2 text-xs">
                              <div className="min-w-0">
                                <div className="truncate font-mono font-medium text-slate-700">{attempt.id}</div>
                                <div className="text-slate-500">{formatShortDate(attempt.created_at)} · {attempt.duration_label || '0s'}</div>
                              </div>
                              <span className={`flex-none ${statusBadgeClass[attempt.status_kind]}`}>
                                {attempt.status_label}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Next action */}
                    {card.next_action && (
                      <div className="mt-3 rounded-lg bg-primary-50 px-2 py-2 text-xs text-primary-900">
                        {card.next_action}
                      </div>
                    )}

                    {/* Preflight result */}
                    {card.preflight && (
                      <div className={`mt-3 flex items-center gap-2 rounded-lg px-2 py-2 text-xs ${
                        card.preflight.status === 'approved'
                          ? 'badge-success bg-success-50 text-success-800'
                          : 'bg-warning-50 text-warning-800'
                      }`}>
                        <ShieldCheck className="h-4 w-4 flex-none" />
                        <span className="min-w-0 break-words">{card.preflight.message}</span>
                      </div>
                    )}

                    {/* Card actions */}
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => void handlePreflight(card)}
                        disabled={preflightingCardId === card.id || !card.agent_id || !card.phone_number}
                        title={(!card.agent_id || !card.phone_number) ? 'Agente e telefone são necessários para preflight' : undefined}
                        className="btn-secondary h-8 px-2 text-xs"
                        aria-label={`Validar preflight do lead ${card.title}`}
                      >
                        {preflightingCardId === card.id ? (
                          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <ShieldCheck className="h-3.5 w-3.5" />
                        )}
                        Preflight
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/app/calls', {
                          state: card.phone_number
                            ? { openNewCall: true, phoneNumber: card.phone_number }
                            : undefined
                        })}
                        className="btn-secondary h-8 px-2 text-xs"
                        aria-label={`Ligar para o lead ${card.title}`}
                      >
                        <Phone className="h-3.5 w-3.5" />
                        Ligar
                      </button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

interface LeadMetricProps {
  label: string
  value: string | number
}

function LeadMetric({ label, value }: LeadMetricProps) {
  return (
    <div className="rounded-md bg-slate-50 px-2 py-2">
      <div className="text-sm font-bold tabular-nums text-slate-950">{value}</div>
      <div className="truncate text-[11px] font-medium text-slate-500">{label}</div>
    </div>
  )
}

interface MetricProps {
  icon: LucideIcon
  label: string
  value: string | number
  iconBg?: string
  compact?: boolean
}

function Metric({ icon: Icon, label, value, iconBg = 'bg-primary-50 text-primary-600', compact = false }: MetricProps) {
  return (
    <div className="metric-card">
      <div className="flex items-center gap-3">
        <div className={`metric-icon ${iconBg}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className={`${compact ? 'truncate text-sm' : 'text-2xl'} font-bold tabular-nums text-slate-950`}>
            {value}
          </p>
        </div>
      </div>
    </div>
  )
}

interface InfoLineProps {
  icon: LucideIcon
  text: string
}

function InfoLine({ icon: Icon, text }: InfoLineProps) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="mt-0.5 h-3.5 w-3.5 flex-none text-slate-400" />
      <span className="min-w-0 truncate">{text}</span>
    </div>
  )
}
